import { useState, useEffect, useContext } from 'react'
import { Box, Slider, Container } from '@mui/material'
import { useChangeMasterVolume, usePostMqttChangeMasterVolume } from '../../../mqtt/mqttHandlerContext'
import Text from '../../../assets/components/Text'
import { useConnectedInstruments, useUpdateConnectedInstrument } from '../../../hooks/contexts/connectedInstrumentsContext'
import { Instrument } from '../../../utils/types'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'
import { VOLUME_MARKS, VOLUME_MAX, VOLUME_MIN, VOLUME_STEP } from '../Instruments/Instruments'

interface MasterVolumeProps {
    instrumentVolumes: { [key: string]: number }
    setInstrumentVolumes: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
    isSmallScreen?: boolean
}

// snap helper so the value is always one of the discrete steps
const snapToStep = (v: number) => {
    const steps = Math.round((v - VOLUME_MIN) / VOLUME_STEP)
    const snapped = VOLUME_MIN + steps * VOLUME_STEP
    return parseFloat(snapped.toFixed(3)) // keep nice decimals (e.g. 0.075)
}

const MasterVolume = ({ instrumentVolumes, setInstrumentVolumes, isSmallScreen = false }: MasterVolumeProps) => {
    const { polyglot } = useContext(PolyglotContext)
    // const postMqttChangeMasterVolume = usePostMqttChangeMasterVolume()
    const changeMasterVolume = useChangeMasterVolume()
    const connectedInstruments = useConnectedInstruments()
    const updateConnectedInstruments = useUpdateConnectedInstrument()

    // initial value: use middle step so it's valid for the marks
    const middle = snapToStep((VOLUME_MIN + VOLUME_MAX) / 2)
    const [sliderValue, setSliderValue] = useState<number>(middle)

    // update local UI while dragging (no MQTT), snap to the nearest step
    const handleSliderChange = (_: Event | React.SyntheticEvent, newValue: number | number[]) => {
        const v = Array.isArray(newValue) ? newValue[0] : newValue
        setSliderValue(snapToStep(v))
    }

    // commit on release: update all instrument volumes + send one MQTT
    const handleSliderCommit = (_: Event | React.SyntheticEvent, newValue: number | number[]) => {
        const v = Array.isArray(newValue) ? newValue[0] : newValue
        const committed = snapToStep(v)
        setSliderValue(committed)

        const updated: Record<string, number> = {}
        connectedInstruments.forEach((instrument: Instrument) => {
            if (instrument.protocol === '1.0') {
                updated[instrument.deviceId] = committed
            } else {
                updateConnectedInstruments({ ...instrument, volume: committed })
            }
        })

        setInstrumentVolumes((prev) => ({ ...prev, ...updated }))

        // one MQTT message after release
        // postMqttChangeMasterVolume(committed.toString())
        changeMasterVolume({ volume: committed })

        // persist
        localStorage.setItem('masterSliderValue', committed.toString())
    }

    // restore persisted value (and snap it)
    useEffect(() => {
        const saved = localStorage.getItem('masterSliderValue')
        if (saved) setSliderValue(snapToStep(parseFloat(saved)))
    }, [])

    return (
        <Container
            sx={{
                paddingTop: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: '100%',
                    justifyContent: 'center'
                }}
            >
                <Slider
                    orientation="vertical"
                    aria-label="Master Volume"
                    value={sliderValue}
                    min={VOLUME_MIN}
                    max={VOLUME_MAX}
                    step={VOLUME_STEP}
                    marks={VOLUME_MARKS}
                    valueLabelDisplay="off"
                    onChange={handleSliderChange}
                    onChangeCommitted={handleSliderCommit}
                    sx={{
                        height: isSmallScreen ? 100 : 140,
                        width: 10,
                        '& .MuiSlider-thumb': {
                            width: 14,
                            height: 14,
                            bgcolor: '#d5e6c3',
                            border: '1px solid #3fad4471',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
                        },
                        '& .MuiSlider-rail': {
                            opacity: 1,
                            background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                            width: 6,
                            borderRadius: 1
                        },
                        '& .MuiSlider-track': {
                            background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                            width: 6,
                            borderRadius: 1,
                            borderColor: '#d5e6c3'
                        },
                        '& .MuiSlider-mark': {
                            width: 6,
                            height: 3,
                            bgcolor: 'rgba(255, 255, 255, 0.25)',
                            borderRadius: 1,
                            left: '50%',
                            transform: 'translateX(-50%)'
                        }
                    }}
                />
                <Text size="standard" textAlign="center">
                    {polyglot.t('home.masterVolume.paragraphMaster')}
                </Text>
            </Box>
        </Container>
    )
}

export default MasterVolume
